/**
 * 1. 처음 접속 시 사이트 어느 페이지에 접근하든 account 정보를 읽어 로그인 정보를 획득 - retainAccountInfo
 * 2. 로그인 정보가 필요한 페이지에 접근하면, 로그인 상태가 아니면 로그인 페이지로 이동
 */
import { useStorage } from '@vueuse/core'
import { jwtDecode } from 'jwt-decode'
import { createMachine, type Sender } from 'xstate'
import { setUser } from '@sentry/vue'

type AuthenticationMachineContext = {
  accountInfo?: AccountInfo
}
type TokenPayload = {
  userId: string
  userName: string
  userLevel: string
  partnerName: string
}
interface AccountInfo {
  accessToken: string
  refreshToken: string
}
const persistAccountInfo = useStorage('accountInfo', {} as AccountInfo)

type AuthenticationMachineEvent = {
  type: 'LOG_IN' | 'LOG_OUT' | 'REFRESH_TOKEN'
  accountInfo?: AccountInfo
}
export type AccountState = 'retainingAccountInfo' | 'loggedIn' | 'loggedOut'
type AuthenticationTypestate = {
  value: AccountState
  context: {}
}

export const accountAuthMachine = createMachine<
  AuthenticationMachineContext,
  AuthenticationMachineEvent,
  AuthenticationTypestate
>(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QEMDGqD2BXAdgFwEEs8ALAWTRIEscwA6AJzD2RpqgPW3wEkcAzDAGIwDBhgZ0ADgBtkeQQwC2dNJlyFi5SjXpMWbHBy4a+ghDQBuGVPKoYcAbQAMAXRevEoKRlhU89jheIAAeiABMAJzhdAAcAOwALJEpSeHOAIwArADMWQA0IACeiNk5dM5ZKc45mRnh8c6J4QC+LYVq3JqkFKjUtIzMrDjsnOq8AsIASgCiAAoA8lMAKgD6PADKqwAyCwDiezMAIusAch7BPn4BDsFhCFExCcmpzZm5BcWIOYnlAGxZP7hdLxcKJDKxaJtDomfBEHo6Ab6YajWF4MzTeZLNabHb7Q4nBYAVWWFyQICu-kCdwi0TiSRSkTS7zyhRKCAyzhiWSqsVi4T+fxyfwygpy0JAnQ08O0fV0dBkGCgMAgfCEuz2q2JpLcl18VNu5Pu8UizjoJr+sWc0XSwMSiTZiFiGToL0isV+OXikLKEqlcK0vX69EVysgC2I6v2ZzJ3n1NyCRsQ8W9cXqyT58UtsRyjo5KdduQyPz+jRy4T5fza7RAOAwEDgwX93Vlwb112pSYQAFo-nnexVnEPreD4hl7VkMhk-WiZUH5cjDMZxujJu2DYnQPdmnnOWbIj8fTyfuEcpFpzXm3PESGlSq+OuEzSENU6D8vZzErEqu6HV985EdClpElpcqKxblq0l6zoGN4Kne4bEI+nZbogWS-K6zhAqWJqxACrL-pEiRAb8dqJFkCRZFhsTVi0QA */
    id: 'accountAuthMachine',
    initial: 'retainingAccountInfo',
    states: {
      retainingAccountInfo: {
        invoke: {
          src: 'retainAccountInfo',
          onError: {
            target: 'loggedOut',
            actions: (_context, event) => console.error(event.data),
          },
        },
        on: {
          LOG_IN: {
            target: 'loggedIn',
            actions: 'refreshAccessToken',
          },
          LOG_OUT: 'loggedOut',
        },
      },
      loggedIn: {
        entry: ['assignAccountInfo'],
        on: {
          LOG_OUT: {
            target: 'loggedOut',
            actions: 'navigateToAuthPage',
          },
          REFRESH_TOKEN: {
            actions: 'refreshAccessToken',
          },
        },
      },
      loggedOut: {
        entry: ['clearAccountInfo'],
        on: {
          LOG_IN: {
            target: 'loggedIn',
            actions: ['navigateToMainPage'],
          },
        },
      },
    },
    predictableActionArguments: true,
  },
  {
    services: {
      retainAccountInfo:
        () => async (send: Sender<AuthenticationMachineEvent>) => {
          if (process.server) return //! server에는 localStorage가 없으므로 밑에서 error 발생
          if (persistAccountInfo.value.accessToken) {
            send({
              type: 'LOG_IN',
              accountInfo: {
                accessToken: persistAccountInfo.value.accessToken,
                refreshToken: persistAccountInfo.value.refreshToken,
              },
            })
          } else {
            send({
              type: 'LOG_OUT',
            })
          }
        },
    },
    actions: {
      navigateToAuthPage: () => navigateNextTo({ path: '/' }),
      navigateToMainPage: () => navigateNextTo({ path: '/dashboard' }),
      assignAccountInfo: (
        _context,
        { type, accountInfo }: AuthenticationMachineEvent,
      ) => {
        if (type !== 'LOG_IN') return
        const auth = useAuth()
        auth.accessToken = accountInfo?.accessToken
        auth.refreshToken = accountInfo?.refreshToken
        const decoded: TokenPayload = jwtDecode(accountInfo!.accessToken)
        auth.id = decoded.userId
        auth.name = decoded.userName
        auth.userLevel = decoded.userLevel === 'A' ? 'admin' : 'general' // A - admin | G - general
        auth.partnerName = decoded.partnerName
        if (process.server) return
        persistAccountInfo.value = accountInfo
        setUser({ id: decoded.userId })
      },
      clearAccountInfo: () => {
        const auth = useAuth()
        auth.accessToken = undefined
        auth.refreshToken = undefined

        if (process.server) return
        persistAccountInfo.value = undefined
      },
      refreshAccessToken: async (
        _context,
        { accountInfo }: AuthenticationMachineEvent,
      ) => {
        if (process.server) return

        const auth = useAuth()
        setUser({ id: auth.id })

        persistAccountInfo.value = accountInfo
      },
    },
  },
)

function navigateNextTo({ path }: { path: string }) {
  setTimeout(() => {
    navigateTo({ path })
  }, 0)
}
